import {
  API_ENDPOINT_PATHS,
  apiFetch,
  getBackendConfig,
  sanitizeEmbedServerConfig,
} from 'ts-core';
import type { EmbedServerConfig } from 'ts-core';

const FETCH_TIMEOUT_MS = 4000;

/**
 * Fetch the partner's server-managed embed config (GET /api/v2/embed/config).
 *
 * Returns null on ANY failure (missing env config, network error, timeout,
 * non-2xx, malformed payload) so the caller can fall back to HTML attributes.
 * Deliberately uncached: admin edits must apply on the next embed load.
 */
export async function fetchEmbedConfig(
  partnerKey: string,
  signal?: AbortSignal
): Promise<EmbedServerConfig | null> {
  try {
    const config = getBackendConfig();
    const configUrl = `${config.baseUrl}${API_ENDPOINT_PATHS.embedConfig}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort('Request timeout'), FETCH_TIMEOUT_MS);

    if (signal) {
      if (signal.aborted) {
        throw new DOMException('Embed config loading was aborted', 'AbortError');
      }
      signal.addEventListener(
        'abort',
        () => {
          controller.abort(signal.reason || 'Operation aborted');
        },
        { once: true }
      );
    }

    const response = await apiFetch(configUrl, {
      apiKey: config.apiKey,
      partnerKey,
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return sanitizeEmbedServerConfig(await response.json());
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.log('[BalloonEventsMapElement] Embed config fetch aborted:', error.message);
    } else {
      console.warn(
        '[BalloonEventsMapElement] Embed config fetch failed, using attribute fallback:',
        error
      );
    }
    return null;
  }
}
